import isString from 'lodash/isString';
import 'numeral/locales';
import numeral from 'numeral';
import makeMoment from '../moment/MomentFactory';
import VcomProvider from '../../VcomProvider';
import MomentFormat from '../moment/MomentFormat';
import translate from './translate';
import Locale from './Locale';
import MetricPrefix from '../unit/MetricPrefix';

function humanizeDuration(milliseconds) {
  if (!Number.isInteger(milliseconds) || milliseconds < 0) {
    return null;
  }

  const hourFactor = 36e5;
  const dayFactor = hourFactor * 24;
  const days = Math.floor(milliseconds / dayFactor);
  const hours = Math.floor((milliseconds % dayFactor) / hourFactor);
  const minutes = Math.floor((milliseconds % hourFactor) / 6e4);

  const displayDays = days > 0;
  const displayHours = (days > 0 && days < 4) || (days === 0 && hours > 0);
  const displayMinutes = days < 4;
  return [
    displayDays ? `${days} ${translate('javascript.TX_DAYS_SHORT')}` : null,
    displayHours ? `${hours} ${translate('javascript.TX_HOURS_SHORT')}` : null,
    displayMinutes ? `${minutes} ${translate('javascript.TX_MINUTES_SHORT')}` : null,
  ].filter(isString).join(' ');
}

function localizeNumber(number, decimalPlaces) {
  const locale = Locale.getLanguageCode();
  const maximumDecimalPlaces = 20;
  const options = decimalPlaces !== undefined ? {
    minimumFractionDigits: Math.min(decimalPlaces, maximumDecimalPlaces),
    maximumFractionDigits: Math.min(decimalPlaces, maximumDecimalPlaces),
  } : {
    maximumFractionDigits: maximumDecimalPlaces,
  };
  return new Intl.NumberFormat(locale, options).format(number);
}

function getMatchingNumericalAbbreviationForNumber(number, remainderAllowed) {
  const divisorToPostfix = {
    [MetricPrefix.EXA]: 'E',
    [MetricPrefix.PETA]: 'P',
    [MetricPrefix.TERA]: 'T',
    [MetricPrefix.GIGA]: 'G',
    [MetricPrefix.MEGA]: 'M',
    [MetricPrefix.KILO]: 'k',
  };
  // eslint-disable-next-line fp/no-mutating-methods
  const sortedDivisorFromBiggestToSmallest = Object.keys(divisorToPostfix).sort((a, b) => b - a);
  const matchingDivisor = sortedDivisorFromBiggestToSmallest.find((divisor) => {
    const noRemainderAfterDivision = (Math.abs(number) * 10) % divisor === 0;
    return Math.abs(number) >= divisor && (remainderAllowed || noRemainderAfterDivision);
  });
  return matchingDivisor ? {
    postfix: divisorToPostfix[matchingDivisor],
    number: localizeNumber(
      (number / matchingDivisor),
      Number.isInteger((number / matchingDivisor)) ? 0 : 2,
    ),
  } : {
    postfix: '',
    number: localizeNumber(number),
  };
}

export default {
  getDecimalSeparator() {
    const locale = Locale.getLanguageCode();
    return new Intl.NumberFormat(locale).format(1.1).charAt(1);
  },

  getDuration(startDateTime, endDateTime) {
    return humanizeDuration((-1) * startDateTime.diff(endDateTime)) || null;
  },

  humanizeDuration(milliseconds) {
    return humanizeDuration(milliseconds);
  },

  localizeDateTimeFormat(dateTime, format = MomentFormat.LOCALE_DATE_TIME) {
    const targetLocale = VcomProvider.LanguageSwitch.getCurrentLocale().replace(/zh_HANS/, 'zh_CN');
    return makeMoment(dateTime).locale(targetLocale).format(format);
  },

  localizeCalendarTime(dateTime) {
    return makeMoment(dateTime).locale(Locale.getLanguageCode()).calendar();
  },

  convertTimezoneFromUtcToUser(dateTime) {
    return makeMoment.utc(dateTime).tz(VcomProvider.userTimeZone);
  },

  localizeElapsedTime(duration) {
    const prefix = translate('javascript.TX_RELATIVE_TIME_PREFIX');
    const suffix = translate('javascript.TX_RELATIVE_TIME_SUFFIX');
    return [prefix, duration, suffix]
      .filter(String)
      .join(' ');
  },

  localizeNumber(number, decimalPlaces) {
    return localizeNumber(number, decimalPlaces);
  },

  localizeNumberWithOptions(number, options) {
    const locale = Locale.getLanguageCode();
    return new Intl.NumberFormat(locale, options).format(number);
  },

  parseNumber(number) {
    const targetLocale = Locale.getLanguageCode();
    numeral.locale(targetLocale.replace(/zh/, 'chs'));
    return numeral(number).value();
  },

  formatNumberWithMetricPostfix(numberToShorten) {
    const { number, postfix } = getMatchingNumericalAbbreviationForNumber(numberToShorten, false);
    return `${number}${postfix}`;
  },

  stringComparator(a, b) {
    const locale = Locale.getLanguageCode();

    return a.localeCompare(b, locale, { sensitivity: 'base' });
  },

  getMatchingNumericalAbbreviationForNumber,
};
